<template>
  <div
    v-show="displayPreviewProject"
    class="preview-project-board-list"
  >
    <NoPagesBoards
      v-if="displayEmptyState"
      :no-pages="noBoards"
      :no-boards="noBoards"
      :no-sprints="noSprints"
      :no-views="false"
    />
    <SprintsHomeItem
      v-if="showSprintsSection"
      class="px-6 mb-2 mt-4"
      :sprint-ids="sprintIds"
      :sprints-cool-down-period="sprintsCoolDownPeriod"
      :space-sprints="spaceSprints"
      :header-text="translate('currentAndNextSprint')"
      :show-icon="true"
      :button-text="translate('seeAllSprints')"
      :button-icon="'arrow-right'"
      :icon-right-position="true"
      :preview-project-boards="true"
      :empty-state-text="translate('thereIsNoCurrentOrNextSprint')"
    />
    <div
      v-if="showBoardsSection"
      class="flex flex-col overflow-hidden w-full"
    >
      <SortHeader
        v-if="displayPreviewProject"
        :resource-type="'boards'"
        :has-resources="hasBoards"
      >
        <SortHeaderTitle
          :title="translate('boards')"
          :button-text="translate('newBoard')"
        />
      </SortHeader>
      <div class="list-of-boards">
        <draggable
          v-model="draggableBoards"
          :item-key="(boardId) => boardId"
          class="draggable-board-list"
          animation="150"
          :fallback-tolerance="5"
          group="boards"
          ghost-class="ghost-board"
          drag-class="drag-board"
          :prevent-on-filter="false"
          :force-fallback="true"
          :class="{ 'draggable-list-grid-view': gridView }"
          :disabled="!displayPreviewProject"
          draggable=".list-of-boards-item-row"
          filter=".__bar-is-horizontal, .__bar-is-vertical"
          @change="changeHandler"
        >
          <template #item="{ element, index }">
            <ListOfBoardsItem
              :key="element"
              :data-boardid="element"
              :item-id="element"
              :board-index="index"
            />
          </template>
        </draggable>
      </div>
      <span
        v-if="!hasBoards"
        class="text-deEmphasised-text px-6 flex text-sm"
      >
        {{ translate('thereAreNoBoards') }}
      </span>
    </div>
  </div>
</template>

<script>
import useSetAndOpenProject from '@/utilities/composables/useSetAndOpenProject';
import ListOfBoardsItem from '@/components/widgets/ListOfBoardsItem';
import NoPagesBoards from '../widgets/NoPagesBoards.vue';
import EventBus from '@/utilities/eventBus';
import SortHeader from '@/components/widgets/SortHeader';
import SortHeaderTitle from '@/components/widgets/SortHeaderTitle';
import SprintsHomeItem from '@/components/sprints/SprintsHomeItem.vue';
import useSpaceHomeSprints from '@/utilities/composables/useSpaceHomeSprints';

export default {
  name: 'PreviewSpaceBoards',
  components: {
    ListOfBoardsItem,
    NoPagesBoards,
    SortHeader,
    SortHeaderTitle,
    SprintsHomeItem,
  },
  props: {
    boardsIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    projectId: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup() {
    const { setAndOpenProject } = useSetAndOpenProject();
    const { spaceSprints, sprintIds, sprintsCoolDownPeriod, spaceSprintsEnabled } =
      useSpaceHomeSprints();

    return {
      setAndOpenProject,
      spaceSprints,
      sprintIds,
      sprintsCoolDownPeriod,
      spaceSprintsEnabled,
    };
  },
  computed: {
    draggableBoards: {
      get() {
        if (!this.displayPreviewProject) return [];
        return this.boardsIds;
      },
      set() {
        // intentionally not using setter
      },
    },
    gridView() {
      return this.$store.getters.getBoardsColumnLayout(this.projectId) === 'grid';
    },
    isCardDragging() {
      return this.$store.getters.draggedEntity.entityName === 'card';
    },
    hasBoards() {
      return this.draggableBoards.length > 0;
    },
    noBoards() {
      return !this.hasBoards && this.isCardDragging;
    },
    currentProjectId() {
      return this.projectId; // using in useSpaceHomeSprints
    },
    displayPreviewProject() {
      return this.projectId === this.$store.getters.previewProject.id;
    },
    noSprints() {
      return !this.spaceSprintsEnabled && this.isCardDragging;
    },
    showBoardsSection() {
      return this.displayPreviewProject && !this.displayEmptyState;
    },
    showSprintsSection() {
      return (
        this.displayPreviewProject &&
        this.spaceSprintsEnabled &&
        this.isCardDragging &&
        !this.displayEmptyState
      );
    },
    displayEmptyState() {
      return this.displayPreviewProject && this.noBoards && this.noSprints;
    },
  },
  methods: {
    changeHandler(evt) {
      if (evt.added) {
        const { element, newIndex } = evt.added;

        this.$store
          .dispatch('moveBoardToProject', {
            boardId: element,
            projectId: this.projectId,
            position: newIndex,
          })
          .then(() => {
            if (this.$store.getters.displayProjectPreviewBoards) {
              const project = this.$store.getters.getMyProjectById(this.projectId);
              if (!project) return;
              this.setAndOpenProject(this.projectId).then(() => {
                this.$store.dispatch('setProjectMembers', project.members);
                this.$store.dispatch('displayProjectPreviewBoards', false);
                this.$store.dispatch('setProjectPreview', {});
                this.$store.dispatch('setDraggedEntity', {});
                this.$store.dispatch('setDropTargetId', '');
                this.$store.dispatch('clearProjectPreviewBoards');
              });
              // Retain scroll position of space after dragging board into it
              EventBus.$emit('saveLastScrollPosition');
            }
          });
      }
    },
  },
};
</script>
<style lang="scss">
.preview-project-board-list {
  @apply bg-background-strongest;

  .draggable-list-grid-view {
    @apply grid grid-cols-2 w-full auto-rows-max;
    gap: $spaceHomePageItemsGap;
  }

  .no-pages {
    min-height: $spacePreviewHeight;
    height: $spacePreviewHeight;
  }
}
</style>
